import { useCallback, useMemo } from 'react';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { includes, isEmpty, isNil, times } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import SpeechBubbles from '@gef-ui/components/atoms/SvgIcon/Icons/SpeechBubbles';
import Unlocked from '@gef-ui/components/atoms/SvgIcon/Icons/Unlocked';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { Analyze, Flame, Gear, Overview, TrafficLights, Users } from 'components/atoms/CustomSVGIcon';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { EMPTY_VALUES } from 'constants/general';
import {
	KCL_DISPLAY_SUBORD_POLICY_LIST,
	KCL_DISPLAY_SUBORD_UNLOCKED_CLIENTS_ICON,
	KCL_DISPLAY_SUBORDINATE_CONTACT_HISTORY,
	KCL_DISPLAY_SUBORDINATE_LEAD,
	KCL_DISPLAY_SUBORDINATE_RA_ANALYSES,
	KCL_DISPLAY_SUBORDINATE_SEMAPHORE,
	KCL_DISPLAY_SUBORDINATE_SETTINGS,
	MPU_DISPLAY_SUBORD_TASK_LIST,
} from 'features/common/acl/constants';
import { getRoot as getCollapsibleSubheaders } from 'features/common/Subheader/selectors';
import { setVisibleState } from 'features/common/Subheader/slice';
import typeTasks from 'features/other/typeTasks';
import contactHistory from 'features/views/contactHistory';
import taskOverview from 'features/views/taskOverview';
import taskOverviewUserContext from 'features/views/taskOverview/taskOverviewUserContext';
import userContext from 'features/views/userContext';
import usersSubordinate from 'features/views/usersSubordinate';
import { SUBORDINATE_USERS_FORM_NAME } from 'features/views/usersSubordinate/form';
import { equalOrThreeDashes, getRoutePath, isUserLoginFormat } from 'utils';
import { joinString } from 'utils/string';

const { QUICK_FILTER_STATES } = taskOverview.constants;

export const SubordinateUsersTableBody = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const next = useControlNext(SUBORDINATE_USERS_FORM_NAME);

	const subordinatesData = useSelector(usersSubordinate.selectors.getUsers);
	const mpuDisplaySuboardTaskList = useSelector((state) => hasPermissionByIds(state)(MPU_DISPLAY_SUBORD_TASK_LIST));
	const subheadersList = useSelector(getCollapsibleSubheaders);

	const tasksOverviewTypeItemsSubheaderIds = useMemo(() => {
		if (!isNilOrEmpty(subheadersList)) {
			return Object.keys(subheadersList).filter((subheaderId) => subheaderId.includes('tasksOverview_type'));
		} else {
			return EMPTY_VALUES.ARRAY;
		}
	}, [subheadersList]);

	const handleTasks = useCallback(
		(user, quickFilter) => () => {
			// reset or close all previously opened TypeItems
			tasksOverviewTypeItemsSubheaderIds.forEach((subheaderId) =>
				dispatch(setVisibleState({ id: subheaderId, visible: false })),
			);
			dispatch(userContext.actions.setContextUser(user));
			dispatch(taskOverviewUserContext.actions.setTaskOverviewUserContext(user));
			dispatch(taskOverview.actions.reset());
			dispatch(typeTasks.actions.reset());
			dispatch(taskOverview.actions.changeQuickFilterView(quickFilter));

			const { pathname, state } = getRoutePath('tasks', EMPTY_VALUES.OBJECT, true);
			navigate(pathname, { state });
		},
		[navigate, dispatch, tasksOverviewTypeItemsSubheaderIds],
	);

	const handleLink = useCallback(
		(user, page) => () => {
			let path;
			switch (page) {
				case 'policyOverview':
					path = page;
					break;
				case 'unlockedClients':
					path = page;
					break;
				case 'marketPlace':
					path = page;
					break;
				case 'roboAdvisory':
					path = page;
					break;
				case 'semaphore':
					path = page;
					break;
				case 'settings':
					path = page;
					break;
			}

			dispatch(userContext.actions.setContextUser(user));
			const { pathname, state } = getRoutePath(path, EMPTY_VALUES.OBJECT, true);
			navigate(pathname, { state });
		},
		[dispatch, navigate],
	);

	const handleSubordinates = useCallback(
		(user) => () => {
			dispatch(userContext.actions.setContextUser(user));
			next(resetForm());
		},
		[dispatch, next],
	);

	const handleKH = useCallback(
		(user) => () => {
			dispatch(userContext.actions.setContextUser(user));
			dispatch(contactHistory.actions.resetCriteria());

			const { pathname, state } = getRoutePath('contactHistory', EMPTY_VALUES.OBJECT, true);
			navigate(pathname, { state });
		},
		[dispatch, navigate],
	);

	const renderIconTooltip = useCallback(({ login, accessPermission, tooltipTitle, icon, onClick }) => {
		let finalIcon;
		switch (icon) {
			case 'flame':
				finalIcon = <Flame />;
				break;
			case 'analyze':
				finalIcon = <Analyze />;
				break;
			case 'overview':
				finalIcon = <Overview />;
				break;
			case 'semaphore':
				finalIcon = <TrafficLights />;
				break;
			case 'users':
				finalIcon = <Users />;
				break;
			case 'unlocked':
				finalIcon = <Unlocked />;
				break;
			case 'settings':
				finalIcon = <Gear />;
				break;
			case 'kh':
				finalIcon = <SpeechBubbles />;
				break;
		}

		if (includes(icon, ['users', 'kh'])) {
			return (
				<Tooltip title={tooltipTitle}>
					<IconButton disableRipple sx={{ color: 'primary.main', fontSize: 15 }} onClick={onClick}>
						{finalIcon}
					</IconButton>
				</Tooltip>
			);
		} else {
			return (
				isUserLoginFormat(login) && (
					<AccessControl accessLevel={accessPermission}>
						<Tooltip title={tooltipTitle}>
							<IconButton disableRipple sx={{ color: 'primary.main', fontSize: 15 }} onClick={onClick}>
								{finalIcon}
							</IconButton>
						</Tooltip>
					</AccessControl>
				)
			);
		}
	}, []);

	const renderEmptyRow = () => (
		<TableRow>
			{times(
				(i) => (
					<TableCell key={i}>---</TableCell>
				),
				5,
			)}
		</TableRow>
	);

	return (
		<TableBody>
			{isNil(subordinatesData) ? (
				renderEmptyRow()
			) : isEmpty(subordinatesData) ? (
				<TableHasNoResults />
			) : (
				subordinatesData.map((item, i) => (
					<TableRow key={i}>
						<TableCell>{equalOrThreeDashes(item.sellerIn)}</TableCell>
						<TableCell sx={{ whiteSpace: 'pre-wrap' }}>
							{joinString(' ', [item?.surname, item?.firstName])}
						</TableCell>
						<TableCell align="center">
							{mpuDisplaySuboardTaskList ? (
								<Link
									sx={{ cursor: 'pointer' }}
									underline="none"
									onClick={handleTasks(item, QUICK_FILTER_STATES.tasks)}
								>
									{joinString('/', [
										item?.taskCountBeforeDeadlineActNoInfo,
										item?.taskCountAfterDeadlineActNoInfo,
									])}
								</Link>
							) : (
								joinString('/', [
									item?.taskCountBeforeDeadlineActNoInfo,
									item?.taskCountAfterDeadlineActNoInfo,
								])
							)}
						</TableCell>
						<TableCell align="center">
							{mpuDisplaySuboardTaskList ? (
								<Link
									sx={{ cursor: 'pointer' }}
									underline="none"
									onClick={handleTasks(item, QUICK_FILTER_STATES.info)}
								>
									{item.taskCountInformative}
								</Link>
							) : (
								item.taskCountInformative
							)}
						</TableCell>
						<TableCell align="center">
							{renderIconTooltip({
								login: item?.login,
								accessPermission: KCL_DISPLAY_SUBORDINATE_LEAD,
								tooltipTitle: getLabelMessage('subordinateUsers:tableMarketPlaceLeads'),
								icon: 'flame',
								onClick: handleLink(item, 'marketPlace'),
							})}
						</TableCell>
						<TableCell align="center">
							{renderIconTooltip({
								login: item?.login,
								accessPermission: KCL_DISPLAY_SUBORDINATE_SEMAPHORE,
								tooltipTitle: getLabelMessage('subordinateUsers:tableSemafor'),
								icon: 'semaphore',
								onClick: handleLink(item, 'semaphore'),
							})}
						</TableCell>
						<TableCell align="center">
							{renderIconTooltip({
								login: item?.login,
								accessPermission: KCL_DISPLAY_SUBORDINATE_RA_ANALYSES,
								tooltipTitle: getLabelMessage('subordinateUsers:tableRaAnalysis'),
								icon: 'analyze',
								onClick: handleLink(item, 'roboAdvisory'),
							})}
						</TableCell>
						<TableCell align="center">
							{renderIconTooltip({
								login: item?.login,
								accessPermission: KCL_DISPLAY_SUBORD_POLICY_LIST,
								tooltipTitle: getLabelMessage('subordinateUsers:policyOverview'),
								icon: 'overview',
								onClick: handleLink(item, 'policyOverview'),
							})}
						</TableCell>
						<TableCell align="center">
							{item.hasSubordinates &&
								renderIconTooltip({
									tooltipTitle: getLabelMessage('subordinateUsers:subordinates'),
									icon: 'users',
									onClick: handleSubordinates(item),
								})}
						</TableCell>
						<TableCell align="center">
							{!item.login.startsWith('repr_groupaa_') && ( // !isGroup
								<AccessControl accessLevel={KCL_DISPLAY_SUBORDINATE_CONTACT_HISTORY}>
									{renderIconTooltip({
										tooltipTitle: getLabelMessage('subordinateUsers:contactHistory'),
										icon: 'kh',
										onClick: handleKH(item),
									})}
								</AccessControl>
							)}
						</TableCell>
						<TableCell align="center">
							{renderIconTooltip({
								login: item?.login,
								accessPermission: KCL_DISPLAY_SUBORD_UNLOCKED_CLIENTS_ICON,
								tooltipTitle: getLabelMessage('subordinateUsers:tableUnlockedClients'),
								icon: 'unlocked',
								onClick: handleLink(item, 'unlockedClients'),
							})}
						</TableCell>
						<TableCell align="center">
							{renderIconTooltip({
								login: item?.login,
								accessPermission: KCL_DISPLAY_SUBORDINATE_SETTINGS,
								tooltipTitle: getLabelMessage('subordinateUsers:tableSettings'),
								icon: 'settings',
								onClick: handleLink(item, 'settings'),
							})}
						</TableCell>
					</TableRow>
				))
			)}
		</TableBody>
	);
};

export default SubordinateUsersTableBody;
